import { configs, type AppConfig } from '../../../shared/components/global-nav/app-config';
import type {
  AvailableAppItem,
  AvailableApps,
} from '../../../shared/components/global-nav/use-available-apps';
import type { LastUsed } from './last-used-storage';

/**
 * URL resolution for the manager shell. The URL is the single source of truth
 * for the active (application, venue) selection; this module decides what a
 * given pathname means and where invalid/incomplete addresses must redirect.
 * Contract: specs/016-global-nav-venue-sync/contracts/manager-url-scheme.md
 */

const managerOwnedRoutes = [
  '/login',
  '/forgot-password',
  '/reset-password',
  '/invited',
  '/forbidden',
  '/account',
];

export const VENUE_ACCESS_NOTICE = "You don't have access to that venue.";

/**
 * Whether two grants carry the same authority, so a same-app switch may keep the current section
 * (FR-006a). Checkd grants have a null `role`, so they compare by `roleId` instead.
 */
export function hasSameRole(
  a: Pick<AvailableAppItem, 'role' | 'roleId'>,
  b: Pick<AvailableAppItem, 'role' | 'roleId'>,
): boolean {
  if (a.roleId && b.roleId) return a.roleId === b.roleId;

  return a.role === b.role;
}

export function isManagerOwnedRoute(pathname: string): boolean {
  return managerOwnedRoutes.some((route) => pathname === route || pathname.startsWith(`${route}/`));
}

export function getAppIdFromPathname(pathname: string): AppConfig['id'] | null {
  const firstSegment = pathname.split('/')[1];
  if (!firstSegment) return null;

  const direct = configs.find((c) => c.id === firstSegment);

  return direct ? direct.id : null;
}

export type SelectionResolution =
  /** Shell-owned route, or an app whose venue list is unavailable — no venue selection applies. */
  | { kind: 'none' }
  /** Grants not settled — render the addressed URL's loading state, no redirect, no notice. */
  | { kind: 'pending' }
  /** URL names a granted application + venue. */
  | { kind: 'selected'; item: AvailableAppItem }
  /** URL is incomplete or invalid — materialise the decision via replace-redirect. */
  | { kind: 'redirect'; to: string; notice?: string };

/**
 * Default application: last-used if the user still has venues there, else the
 * first application (in the selector's deterministic order) they have venues
 * for (FR-010, clarification 2026-07-22 Q1).
 */
export function resolveDefaultAppId(
  availableApps: AvailableAppItem[],
  lastUsed: LastUsed,
): string | null {
  if (lastUsed.lastApp && availableApps.some((a) => a.application === lastUsed.lastApp)) {
    return lastUsed.lastApp;
  }

  return availableApps[0]?.application ?? null;
}

/** Default venue within an app: last-used if still granted, else the first. */
function resolveDefaultVenueItem(
  matches: AvailableAppItem[],
  appId: string,
  lastUsed: LastUsed,
): AvailableAppItem | undefined {
  const preferredVenueId = lastUsed.lastVenueByApp[appId];
  return (preferredVenueId && matches.find((m) => m.venue?.id === preferredVenueId)) || matches[0];
}

export function resolveSelection(
  pathname: string,
  availableApps: AvailableApps,
  lastUsed: LastUsed,
): SelectionResolution {
  if (isManagerOwnedRoute(pathname)) {
    return { kind: 'none' };
  }

  const appId = getAppIdFromPathname(pathname);

  // FR-013 rule 0: an unsettled list may omit venues the user holds — a non-empty one included.
  if (!availableApps.isSettled) {
    return { kind: 'pending' };
  }

  const { items, unsettledAppIds, unavailableAppIds } = availableApps;
  if (appId && unsettledAppIds.includes(appId)) {
    return { kind: 'pending' };
  }

  if (appId && unavailableAppIds.includes(appId)) {
    return { kind: 'none' };
  }

  // Missing or unknown application, or an application the user has no venues
  // for → the default application (its venue resolves on the next pass).
  const matches = appId ? items.filter((a) => a.application === appId) : [];
  if (!appId || matches.length === 0) {
    if (lastUsed.lastApp && (unsettledAppIds as string[]).includes(lastUsed.lastApp)) {
      return { kind: 'pending' };
    }
    const defaultAppId = resolveDefaultAppId(items, lastUsed);
    return defaultAppId ? { kind: 'redirect', to: `/${defaultAppId}` } : { kind: 'pending' };
  }

  const venueSlug = pathname.split('/')[2];

  if (!venueSlug) {
    const item = resolveDefaultVenueItem(matches, appId, lastUsed);
    return item ? { kind: 'redirect', to: `/${appId}/${item.venue.slug}` } : { kind: 'pending' };
  }

  const selected = matches.find((m) => m.venue?.slug === venueSlug);
  if (selected) {
    return { kind: 'selected', item: selected };
  }

  // Settled grants lacking the addressed venue (revoked/nonexistent): default venue + notice (Q2).
  const fallback = resolveDefaultVenueItem(matches, appId, lastUsed);
  return fallback
    ? {
        kind: 'redirect',
        to: `/${appId}/${fallback.venue.slug}`,
        notice: VENUE_ACCESS_NOTICE,
      }
    : { kind: 'pending' };
}
